import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text } from '@repletoryreach/sdk-ui';
import { EntityCardGrid, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate } from '../../../lib/format';
import { TenantName } from '../../../components/TenantName';
import { PersonAvatar } from './PersonAvatar';
import { useT } from '../../../i18n/I18nProvider';

type AdminUser = NonNullable<Awaited<ReturnType<typeof admin.listUsers>>['data']>[number];

export function UsersGrid({ records }: { records: AdminUser[] }) {
  const navigate = useNavigate();
  const t = useT();
  const unnamed = t('users.unnamed');
  return (
    <EntityCardGrid<AdminUser>
      records={records}
      idAccessor="id"
      cols={{ base: 1, sm: 2, lg: 3 }}
      spacing="md"
      onCardClick={(row) => navigate(`/users/${row.id}`)}
      renderCard={(u) => (
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="sm" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
              <PersonAvatar name={u.display_name || u.email || unnamed} size="md" />
              <Stack gap="xs" style={{ minWidth: 0 }}>
                <Text role="subtitle" fw={600} truncate>{u.display_name || u.email || unnamed}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)" truncate>{u.email ?? unnamed}</Text>
              </Stack>
            </Group>
            <StatusBadge size="xs" status={u.scope === 'platform' ? 'warning' : 'pending'}>
              {u.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
            </StatusBadge>
          </Group>
          <Group gap="md" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Stat label={t('common.tenant')} value={<TenantName id={u.organisation_id} />} />
            <Stat label={t('common.created')} value={formatDate(u.created_at)} />
          </Group>
        </Stack>
      )}
    />
  );
}

/**
 * The value slot can hold a component that renders its own paragraph, such as
 * `TenantName`, so it is a plain block rather than a `<Text>`: a paragraph
 * nested in a paragraph is closed early by the browser and the card breaks.
 */
function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap="xs" style={{ minWidth: 0 }}>
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <div style={{ fontSize: 13, fontWeight: 600, minWidth: 0, overflow: 'hidden' }}>{value}</div>
    </Stack>
  );
}
